"use client";

import { IResource } from "@gettactic/api";
import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTacticHooks } from "../../providers/TacticHooksProvider";

/** An office's meeting rooms, grouped under the floor they sit on. */
export interface OfficeRooms {
  /** Floors that hold at least one room, in the order the API returns them. */
  floors: IResource[];
  /** Rooms keyed by their floor id. Rooms without a floor are not included. */
  roomsByFloorId: Record<string, IResource[]>;
  /** Rooms whose `parent_id` matches no floor — shown directly under the office. */
  roomsWithoutFloor: IResource[];
}

export interface UseOfficeRoomsResult {
  byOfficeId: Record<string, OfficeRooms>;
  /** Offices whose rooms are still in flight. */
  loadingOfficeIds: Set<string>;
}

const EMPTY: OfficeRooms = {
  floors: [],
  roomsByFloorId: {},
  roomsWithoutFloor: []
};

const groupByFloor = (resources: IResource[]): OfficeRooms => {
  const floors = resources.filter((resource) => resource.type === "area");
  const rooms = resources.filter(
    (resource) => resource.type === "meeting_room"
  );
  const floorIds = new Set(floors.map((floor) => floor.id));

  const roomsByFloorId: Record<string, IResource[]> = {};
  const roomsWithoutFloor: IResource[] = [];
  for (const room of rooms) {
    if (room.parent_id && floorIds.has(room.parent_id)) {
      const floorRooms = roomsByFloorId[room.parent_id] ?? [];
      floorRooms.push(room);
      roomsByFloorId[room.parent_id] = floorRooms;
    } else {
      roomsWithoutFloor.push(room);
    }
  }

  return {
    floors: floors.filter((floor) => roomsByFloorId[floor.id]?.length),
    roomsByFloorId,
    roomsWithoutFloor
  };
};

/**
 * Meeting rooms for the given offices, one cached query per office so callers
 * can widen the set as the user drills in without refetching what they already
 * have. Rooms come back grouped by floor, since a room's identity ("Boardroom")
 * only reads unambiguously next to the floor it is on.
 */
export function useOfficeRooms(officeIds: string[]): UseOfficeRoomsResult {
  const { api } = useTacticHooks();
  // Sorted and de-duplicated so a re-render with the same offices in a
  // different order does not rebuild the query list.
  const idsKey = Array.from(new Set(officeIds)).sort().join(",");
  const ids = useMemo(() => (idsKey ? idsKey.split(",") : []), [idsKey]);

  const results = useQueries({
    queries: ids.map((officeId) => ({
      queryKey: ["organizations.resourcesAll", "office-rooms", officeId],
      queryFn: async () => {
        const res = await api.client.organizations.getAllResources({
          officeId,
          types: ["area", "meeting_room"]
        });
        return groupByFloor(res.result?.elements ?? []);
      }
    }))
  });

  return useMemo(() => {
    const byOfficeId: Record<string, OfficeRooms> = {};
    const loadingOfficeIds = new Set<string>();
    ids.forEach((officeId, index) => {
      const result = results[index];
      if (result?.isPending) {
        loadingOfficeIds.add(officeId);
        return;
      }
      byOfficeId[officeId] = result?.data ?? EMPTY;
    });
    return { byOfficeId, loadingOfficeIds };
  }, [ids, results]);
}
